import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, KeyRound, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { API_BASE } from '../config';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white';

const AccountSettings: React.FC = () => {
  const navigate = useNavigate();

  // Change password
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwOk, setPwOk] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  // Delete account
  const [showDelete, setShowDelete] = useState(false);
  const [delPw, setDelPw] = useState('');
  const [delErr, setDelErr] = useState('');
  const [delBusy, setDelBusy] = useState(false);

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwErr('');
    setPwOk(false);
    if (nw !== confirm) { setPwErr('New passwords do not match'); return; }
    if (nw.length < 8) { setPwErr('New password must be at least 8 characters'); return; }
    setPwBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ current_password: cur, new_password: nw }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Could not change password');
      }
      setPwOk(true);
      setCur(''); setNw(''); setConfirm('');
      setTimeout(() => setPwOk(false), 3000);
    } catch (err: unknown) {
      setPwErr(err instanceof Error ? err.message : 'Could not change password');
    } finally {
      setPwBusy(false);
    }
  };

  const deleteAccount = async () => {
    setDelErr('');
    setDelBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: delPw }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Could not delete account');
      }
      navigate('/signup');
    } catch (err: unknown) {
      setDelErr(err instanceof Error ? err.message : 'Could not delete account');
    } finally {
      setDelBusy(false);
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
    >
      <div className="p-6 border-b border-gray-200 dark:border-slate-800">
        <div className="flex items-center space-x-2">
          <Lock className="w-5 h-5 text-gray-600 dark:text-slate-400" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Account</h3>
        </div>
      </div>
      <div className="p-6 space-y-8">
        {/* Change password */}
        <form onSubmit={changePassword} className="space-y-3 max-w-md">
          <h4 className="font-medium text-gray-900 dark:text-white">Change password</h4>
          {pwErr && (
            <div className="flex items-center space-x-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4" /><span>{pwErr}</span>
            </div>
          )}
          {pwOk && (
            <div className="flex items-center space-x-2 text-sm text-emerald-600 dark:text-emerald-400">
              <CheckCircle className="w-4 h-4" /><span>Password changed</span>
            </div>
          )}
          <input type="password" required value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" className={inputClass} />
          <input type="password" required minLength={8} value={nw} onChange={(e) => setNw(e.target.value)} placeholder="New password (min 8 chars)" className={inputClass} />
          <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className={inputClass} />
          <button type="submit" disabled={pwBusy} className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm">
            <KeyRound className="w-4 h-4" />
            <span>{pwBusy ? 'Updating…' : 'Update password'}</span>
          </button>
        </form>

        {/* Danger zone */}
        <div className="border-t border-gray-200 dark:border-slate-800 pt-6 max-w-md">
          <h4 className="font-medium text-red-600 dark:text-red-400">Delete account</h4>
          <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
            Permanently deletes your account and all associated data. This cannot be undone.
          </p>
          {!showDelete ? (
            <button
              onClick={() => setShowDelete(true)}
              className="flex items-center space-x-2 mt-3 px-4 py-2 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
            >
              <Trash2 className="w-4 h-4" /><span>Delete my account</span>
            </button>
          ) : (
            <div className="mt-3 space-y-3">
              {delErr && (
                <div className="flex items-center space-x-2 text-sm text-red-600 dark:text-red-400">
                  <AlertCircle className="w-4 h-4" /><span>{delErr}</span>
                </div>
              )}
              <input
                type="password"
                value={delPw}
                onChange={(e) => setDelPw(e.target.value)}
                placeholder="Enter your password to confirm"
                className="w-full px-3 py-2 border border-red-300 dark:border-red-800 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-slate-800 dark:text-white"
              />
              <div className="flex space-x-2">
                <button
                  onClick={deleteAccount}
                  disabled={delBusy || !delPw}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                >
                  {delBusy ? 'Deleting…' : 'Permanently delete'}
                </button>
                <button
                  onClick={() => { setShowDelete(false); setDelPw(''); setDelErr(''); }}
                  className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-lg text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AccountSettings;
